import { useCallback } from 'react';
import { DiscoveryApi, FetchApi, useApi } from '@backstage/frontend-plugin-api';
import {
  Configuration,
  MtlsRequiredApi as InternalApi,
} from '@internal/backstage-plugin-neo4j-graph-analytics-common';
import type {
  InternalModelsGetRequest,
  InternalSessionsGetRequest,
} from '@internal/backstage-plugin-neo4j-graph-analytics-common';
import type {
  AGASessionsAPI,
  ModelDetailResponse,
  ModelsListResponse,
  SessionDetailResponse,
  SessionsListResponse,
  SessionTelemetryResponse,
} from './types';
import { k8sClustersApiRef } from '@internal/plugin-neo4j-k8s-clusters';

export class AGASessionsClient implements AGASessionsAPI {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private internalApiPromise?: Promise<InternalApi>;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private getApi(): Promise<InternalApi> {
    if (!this.internalApiPromise) {
      this.internalApiPromise = this.discoveryApi.getBaseUrl('proxy').then(
        proxyUrl =>
          new InternalApi(
            new Configuration({
              basePath: `${proxyUrl}/gds-api`,
              fetchApi: async (input: RequestInfo | URL, init?: RequestInit) =>
                this.fetchApi.fetch(input, init),
            }),
          ),
      );
    }

    return this.internalApiPromise;
  }

  async listSessions(
    params?: InternalSessionsGetRequest,
  ): Promise<SessionsListResponse> {
    try {
      const api = await this.getApi();
      const response = await api.internalSessionsGet(params || {});
      return {
        sessions: response.data?.sessions || [],
        totalCount: response.data?.totalCount || 0,
      };
    } catch (error) {
      throw new Error(`Failed to fetch sessions: ${error}`);
    }
  }

  async getSession(sessionId: string): Promise<SessionDetailResponse> {
    try {
      const api = await this.getApi();
      const response = await api.internalSessionsSessionIdGet({ sessionId });
      return response.data ?? {};
    } catch (error) {
      throw new Error(`Failed to fetch session ${sessionId}: ${error}`);
    }
  }

  async getSessionTelemetry(
    sessionId: string,
  ): Promise<SessionTelemetryResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('neo4j-graph-analytics');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/sessions/${sessionId}/telemetry`,
    );

    if (!response.ok) {
      return {};
    }

    return response.json();
  }

  async listModels(
    params?: InternalModelsGetRequest,
  ): Promise<ModelsListResponse> {
    try {
      const api = await this.getApi();
      const response = await api.internalModelsGet(params || {});
      return {
        models: response.data?.models || [],
        totalCount: response.data?.totalCount || 0,
      };
    } catch (error) {
      throw new Error(`Failed to fetch models: ${error}`);
    }
  }

  async getModel(modelId: string): Promise<ModelDetailResponse> {
    try {
      const api = await this.getApi();
      const response = await api.internalModelsIdGet({ id: modelId });
      return response.data ?? {};
    } catch (error) {
      throw new Error(`Failed to fetch model ${modelId}: ${error}`);
    }
  }
}

export const GCP_PROJECT_FALLBACK = 'neo4j-cloud';

/**
 * Resolves the GCP project ID for a given orchestra name by looking it up
 * in the isolation units via the clusters plugin API.
 * Falls back to GCP_PROJECT_FALLBACK if the orchestra cannot be found.
 */
export const useGcpProjectForOrchestra = () => {
  const k8sClustersApi = useApi(k8sClustersApiRef);

  const resolveGcpProject = useCallback(
    async (orchestra: string): Promise<string> => {
      try {
        const result = await k8sClustersApi.listIsolationUnits();
        if (!result.ok || !result.value?.items) {
          // eslint-disable-next-line no-console
          console.warn(
            `Could not fetch isolation units to resolve GCP project for orchestra "${orchestra}": ${result.message}. Falling back to "${GCP_PROJECT_FALLBACK}".`,
          );
          return GCP_PROJECT_FALLBACK;
        }

        for (const unit of result.value.items) {
          for (const region of unit.regions ?? []) {
            for (const cluster of region.clusters ?? []) {
              if (cluster.name === orchestra) {
                const projectId = unit.resources?.gcpProjectId;
                if (projectId) {
                  return projectId;
                }
                // eslint-disable-next-line no-console
                console.warn(
                  `Orchestra "${orchestra}" found but its isolation unit has no GCP project ID. Falling back to "${GCP_PROJECT_FALLBACK}".`,
                );
                return GCP_PROJECT_FALLBACK;
              }
            }
          }
        }

        // eslint-disable-next-line no-console
        console.warn(
          `Orchestra "${orchestra}" not found in any isolation unit. Falling back to "${GCP_PROJECT_FALLBACK}".`,
        );
        return GCP_PROJECT_FALLBACK;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(
          `Error resolving GCP project for orchestra "${orchestra}": ${err}. Falling back to "${GCP_PROJECT_FALLBACK}".`,
        );
        return GCP_PROJECT_FALLBACK;
      }
    },
    [k8sClustersApi],
  );

  return { resolveGcpProject };
};
